import { makeAutoObservable } from 'mobx'

import { Student, TuitionByMonthly } from '..'
import { Month } from '../common/Calendar/Month'
import Tuition from './Tuition'

type Depth1Key = {
  studentId: Student['id']
}
type Depth2Key = Depth1Key & {
  month: Month
}
type Depth3Key = Depth2Key & {
  tuitionId: Tuition['id']
}

// 2 Depth
type TuitionFromStudentId = Map<TuitionByMonthly['dateKey'], TuitionByMonthly>

/***
 * 3 Depth: 학생Id -> yyyy-MM -> (TuitionId -> Tuition) ()가 TuitionByMonthly
 * 학생Id로 조회하는 경우가 제일 많기 때문에...
 *
 * ex)
 * Map {
 *  'I1': {
 *    '2021-08-05' TuitionByMonthly::Map {
 *      84723891: Tuition {},
 *      84723892: Tuition {}
 *    },
 *    '2021-08-06' Map {
 *      ...
 *    }
 *  }
 *  'I2': {
 *    ...
 *  }
 * }
 ***/
export class TuitionByStudentId {
  private tuitionByStudentId = new Map<Student['id'], TuitionFromStudentId>()

  constructor(data: res.YearTuitions[]) {
    makeAutoObservable(this)
    this.updateFromTuitionList(data)
  }

  get firstStudentId(): Student['id'] | undefined {
    return this.tuitionByStudentId.keys().next().value
  }

  updateFromTuitionList(dataList: res.YearTuitions[]) {
    for (const data of dataList) {
      this.addStudentMonthTuition(data)
    }
  }

  // 0 Depth
  get() {
    return this.tuitionByStudentId
  }

  // 1 Depth
  getTuitionFromStudentId({ studentId }: Depth1Key) {
    return this.tuitionByStudentId.get(studentId)
  }

  // 2 Depth
  getTuitionFromMonth({ month, ...depth1key }: Depth2Key) {
    return this.getTuitionFromStudentId(depth1key)?.get(month.toString)
  }

  // 3 Depth
  getTuition({ tuitionId, ...depth2key }: Depth3Key) {
    return this.getTuitionFromMonth(depth2key)?.get(tuitionId)
  }

  deleteTuition(tuition: Tuition) {
    const month = new Month(tuition.paymentDate)
    this.getTuitionFromMonth({ month, studentId: tuition.studentId })?.delete(tuition.id)
  }

  // 3 Depth Data 형태로 추가
  // from: yyyy-mm-dd -> (Attendance + studentId) (서버 데이터)
  addTuition(tuition: Tuition) {
    const month = new Month(tuition.paymentDate)

    if (!this.tuitionByStudentId.has(tuition.studentId)) {
      this.tuitionByStudentId.set(tuition.studentId, new Map())
    }

    const byMonth = this.tuitionByStudentId.get(tuition.studentId)!
    if (!byMonth.has(month.toString)) {
      byMonth.set(month.toString, new TuitionByMonthly(month))
    }
    const tuitionColl = byMonth.get(month.toString)!

    // 3) set attendanceId -> attendance
    tuitionColl.set(tuition.id, tuition)
  }

  // to: 학생Id -> yyyy-MM-dd -> AttendanceId -> Attendance
  private addStudentMonthTuition({ tuitionList }: res.YearTuitions) {
    for (const tuitionData of tuitionList) {
      const tuition = new Tuition(tuitionData)
      this.addTuition(tuition)
    }
  }
}
